/**
 * The Alarms explore page, shared by all three report trees.
 *
 * `variant='single'` (dataset) renders Datetime | Category | Description for one boat.
 * `variant='fleet'`  (day, live) adds a Source column and merges every boat on the date.
 *
 * Ported from Alinghi's AlarmsReportSection with the AC40-specific half removed: RaceSight's rows
 * already arrive in final shape from one parquet family, so there is no messages_/alarms_ merge, no
 * column translation and no `State` suppression.
 *
 * TWO INDEPENDENT MAP GATES. `persistantStore.alarmsExploreOpen` says this page is mounted (that is
 * how a map in the other split pane knows to enable its alarm layer); the local "Show on map"
 * checkbox decides whether we publish any markers. Either being off means no dots.
 *
 * One deliberate divergence from Alinghi: there, the playback-window narrowing was applied only
 * when the map checkbox was ticked, so a map option silently changed which rows the TABLE showed.
 * Here the checkbox controls markers only.
 */

import { onMount, onCleanup, createSignal, createMemo, createEffect, on, untrack, Show, For, batch } from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import { FiSearch } from 'solid-icons/fi';
import { persistantStore } from '../../store/persistantStore';
import {
  selectedRange,
  setSelectedRange,
  setHasSelection,
  setTriggerSelection,
  cutEvents,
} from '../../store/selectionStore';
import {
  setSelectedTime,
  requestTimeControl,
  setIsManualTimeChange,
  releaseTimeControl,
  timeWindow,
  shouldApplyPlaybackTimeWindow,
  getDisplayWindowReferenceTime,
  selectedTime,
  liveMode,
  seekLive,
} from '../../store/playbackStore';
import { formatDateTime } from '../../utils/global';
import { error as logError, debug } from '../../utils/console';
import { logPageLoad } from '../../utils/logging';
import {
  getBoundingMsFromSelectedRange,
  getPlaybackTimeWindowBoundsMs,
} from '../../utils/cutSelectionTimeRange';
import { alarmRowKey, compactAlarmDateYmd, fetchAlarmsForSource, fetchFleetAlarms, resolveAlarmQueryDate, type AlarmRow } from '../../services/alarmsService';
import { fetchDatasetInfo } from '../../services/datasetInfoService';
import { buildAlarmExploreMapMarkers, resolveAlarmCategory } from '../../utils/alarmsExploreMapMarkers';
import { setAlarmsExploreMapMarkers } from '../../store/alarmsExploreMapMarkersStore';

const { selectedClassName, selectedProjectId, selectedDatasetId, selectedDate, selectedSourceName } =
  persistantStore;

/** Keep the DOM small so the table stays responsive with thousands of rows. */
const PAGE_SIZE = 400;

/** Above this many rows, yield between filter and sort so the UI can paint. */
const LARGE_ROW_YIELD_THRESHOLD = 2500;

/** Ctrl/Cmd+click on a row time brushes +/- this many ms (60 s total). */
const CTRL_SEEK_HALF_SPAN_MS = 30_000;

/** Pixels from the scroll bottom still treated as "at bottom". */
const TABLE_BOTTOM_SLACK_PX = 16;

/**
 * Live re-read cadence. Matches alarmPersister's own 10 s flush: polling faster cannot surface
 * anything new, because nothing reaches the parquet until that flush lands.
 */
const LIVE_REFRESH_MS = 10_000;

/** Category filter values. Info is every individual foil movement, so it is hidden by default. */
const FILTER_ALARMS_ONLY = 'alarms_only';
const FILTER_ALL = 'all';

async function yieldToMain(): Promise<void> {
  const g = globalThis as unknown as { scheduler?: { yield: () => Promise<void> } };
  if (g.scheduler?.yield) {
    await g.scheduler.yield();
    return;
  }
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve());
  });
}

export interface AlarmsReportSectionProps {
  variant?: 'single' | 'fleet';
  /** Live mode: newest alarms first, and re-read on an interval. */
  live?: boolean;
  [key: string]: unknown;
}

export default function AlarmsReportSection(props: AlarmsReportSectionProps) {
  const isFleet = () => props.variant === 'fleet';
  const isLive = () => props.live === true;

  /** Store + reconcile so live 10s polls keep <For> row identity instead of rebuilding the table. */
  const [rows, setRows] = createStore<AlarmRow[]>([]);
  const [loading, setLoading] = createSignal(true);
  const [loadError, setLoadError] = createSignal<string | null>(null);
  /**
   * Dataset explore clears `selectedDate`. null = the dataset date is still loading;
   * '' = the lookup finished without a day; YYYYMMDD = the folder to read.
   */
  const [datasetQueryDate, setDatasetQueryDate] = createSignal<string | null>(null);
  const [searchTerm, setSearchTerm] = createSignal('');
  const [categoryFilter, setCategoryFilter] = createSignal(FILTER_ALARMS_ONLY);
  const [showOnMap, setShowOnMap] = createSignal(false);
  const [pageIndex, setPageIndex] = createSignal(0);
  const [tableAtBottom, setTableAtBottom] = createSignal(false);

  let tableScrollEl: HTMLDivElement | undefined;
  let abortController: AbortController | null = null;
  /**
   * True while a read is outstanding, so the live poll can skip a tick instead of cancelling it.
   *
   * The fleet read is one DuckDB query per boat behind a fan-out of four, and on a busy day it
   * regularly takes longer than LIVE_REFRESH_MS. See the interval in onMount.
   */
  let inFlight = false;
  /** After the first successful load, a live poll updates in place rather than showing a spinner. */
  let hasLoadedOnce = false;
  /** Signature of the last marker set published, so an unchanged set is not republished. */
  let lastMarkerSignature = '';
  /** Bumped on every load so a late response from a previous context cannot overwrite a newer one. */
  let fetchGeneration = 0;

  onMount(() => {
    // Tell the maps an Alarms page is mounted (see the header).
    persistantStore.setAlarmsExploreOpen(true);
    void logPageLoad('AlarmsReportSection.tsx', 'Alarms', 'Loaded');

    // Live: re-read on an interval. There is no websocket alarm topic - the stream server writes
    // the parquet and this polls it - so the cadence is tied to the writer's flush, not the UI.
    //
    // A TICK MUST NEVER CANCEL THE READ IN FLIGHT. This used to bump a nonce the load effect read,
    // and the effect aborts on every re-run - so once the fleet read took longer than this
    // interval, each tick killed the read the previous tick had started and the fetch never
    // completed. The table then froze on whatever its first load returned, for the rest of the
    // session, and only a page reload moved it. The tell in the console was a burst of exactly
    // FLEET_SOURCE_FETCH_CONCURRENCY (4) "[alarms] channel-values failed" lines every 10 s: the
    // four in-flight source reads being aborted, over and over.
    if (!isLive()) {return;}
    const timer = setInterval(() => {
      if (inFlight) {return;}
      runLoad(untrack(loadContext));
    }, LIVE_REFRESH_MS);
    onCleanup(() => clearInterval(timer));
  });

  onCleanup(() => {
    fetchGeneration += 1;
    persistantStore.setAlarmsExploreOpen(false);
    setAlarmsExploreMapMarkers([]);
    abortController?.abort();
  });

  /* ---- load ------------------------------------------------------------------------------- */

  /**
   * UTC YYYYMMDD of today. Live mode uses this when no day is selected: alarmPersister partitions
   * by UTC day (alarmPersister.utcDayOfEpochSeconds), so the local day is the wrong folder either
   * side of midnight UTC.
   */
  const todayUtcYmd = () => {
    const d = new Date();
    return `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, '0')}${String(d.getUTCDate()).padStart(2, '0')}`;
  };
  /** Dataset explore has a dataset id and no selected day. The day and live pages do not. */
  const needsDatasetDate = () =>
    !isLive()
    && !isFleet()
    && !compactAlarmDateYmd(selectedDate?.())
    && Number(selectedDatasetId?.() ?? 0) > 0;
  const dateYmd = () => resolveAlarmQueryDate({
    live: isLive(),
    selectedDate: selectedDate?.(),
    datasetDate: needsDatasetDate() ? datasetQueryDate() : '',
    todayUtcYmd: todayUtcYmd(),
  });

  // Selecting a dataset clears selectedDate. The alarms folder is that dataset's day, not today.
  createEffect(on(
    () => {
      if (!needsDatasetDate()) {return '';}
      const className = selectedClassName?.() ?? '';
      const projectId = selectedProjectId?.();
      const datasetId = Number(selectedDatasetId?.() ?? 0);
      if (!className || projectId == null || !datasetId) {return '';}
      return `${className}|${Number(projectId)}|${datasetId}`;
    },
    (key) => {
      if (!key) {
        setDatasetQueryDate(null);
        return;
      }
      setDatasetQueryDate(null);
      let cancelled = false;
      onCleanup(() => { cancelled = true; });
      const [className, projectId, datasetId] = key.split('|');
      void fetchDatasetInfo(className, Number(projectId), Number(datasetId))
        .then((res) => {
          if (cancelled) {return;}
          const ymd = compactAlarmDateYmd(res?.data?.date);
          if (!ymd) {
            logError('[Alarms] dataset has no date', { datasetId });
            setDatasetQueryDate('');
            setLoadError('Could not resolve the dataset date');
            setLoading(false);
            return;
          }
          setLoadError(null);
          setDatasetQueryDate(ymd);
        })
        .catch((err) => {
          if (cancelled) {return;}
          logError('[Alarms] dataset date failed', err);
          setDatasetQueryDate('');
          setLoadError((err as Error)?.message || 'Could not resolve the dataset date');
          setLoading(false);
        });
    }
  ));

  /**
   * Everything the read depends on, in one tracked place.
   *
   * Separated from `runLoad` so the two reasons to re-read stay distinguishable. A change HERE
   * invalidates the read in flight - it is for a different boat, day or cut - so that one is
   * aborted. A live poll is not a context change and must not abort anything; see onMount.
   */
  const loadContext = createMemo(() => ({
    className: selectedClassName?.(),
    projectId: selectedProjectId?.(),
    fleet: isFleet(),
    ymd: dateYmd(),
    awaitingDatasetDate: needsDatasetDate() && datasetQueryDate() === null,
    datasetId: selectedDatasetId?.(),
    sourceName: selectedSourceName?.() ?? '',
    live: isLive(),
    // Re-fetch when the cut changes, matching the other explore pages.
    cut: cutEvents(),
  }));

  type LoadContext = ReturnType<typeof loadContext>;

  /** Read one context's alarms. Reads no signals, so it is safe to call from a timer. */
  const runLoad = (ctx: LoadContext) => {
    const { className, projectId, fleet, ymd, awaitingDatasetDate, datasetId, sourceName, live } = ctx;

    if (awaitingDatasetDate) {
      setLoading(true);
      return;
    }
    if (!className || projectId == null) {return;}
    if (!ymd || (!fleet && !(datasetId && sourceName))) {
      setRows(reconcile([] as AlarmRow[], { key: alarmRowKey }));
      setLoading(false);
      return;
    }

    const gen = ++fetchGeneration;
    const controller = new AbortController();
    abortController = controller;
    const signal = controller.signal;
    inFlight = true;

    // A live poll refreshes in place: flipping to the loading state every 10 s would blank the
    // table and throw away the scroll position. Only the first load of a context shows it.
    if (!hasLoadedOnce) {setLoading(true);}
    setLoadError(null);

    void (async () => {
      try {
        const next = fleet
          ? await fetchFleetAlarms(className, Number(projectId), ymd, signal)
          : await fetchAlarmsForSource({
              className,
              projectId: Number(projectId),
              date: ymd,
              sourceName,
              signal,
            });
        if (signal.aborted || gen !== fetchGeneration) {return;}
        if (next.length > LARGE_ROW_YIELD_THRESHOLD) {await yieldToMain();}
        if (gen !== fetchGeneration) {return;}
        // Live reads newest-first: the interesting row is the one that just arrived, and it should
        // not require scrolling to the bottom of the day to see it.
        const sorted = next.slice().sort((a, b) => (live ? b.ts - a.ts : a.ts - b.ts));
        setRows(reconcile(sorted, { key: alarmRowKey }));
        debug('[Alarms] loaded', { rows: next.length, fleet, ymd });
      } catch (err) {
        if (signal.aborted || gen !== fetchGeneration) {return;}
        logError('[Alarms] load failed', err);
        setLoadError((err as Error)?.message || 'Failed to load alarms');
        setRows(reconcile([] as AlarmRow[], { key: alarmRowKey }));
      } finally {
        // Only the CURRENT read may clear these: an aborted predecessor settles after its
        // replacement has started, and clearing `inFlight` there would re-open the poll on a read
        // that is still running - the same cancel-loop by another route.
        if (gen === fetchGeneration) {
          inFlight = false;
          hasLoadedOnce = true;
          setLoading(false);
        }
      }
    })();
  };

  createEffect(() => {
    const ctx = loadContext();
    // The read in flight is for the previous context, so it is worthless now - abort it.
    abortController?.abort();
    abortController = null;
    inFlight = false;
    runLoad(ctx);
  });

  /* ---- filter ----------------------------------------------------------------------------- */

  /** Distinct categories present, so the dropdown reflects the data rather than a fixed ladder. */
  const uniqueCategories = createMemo(() => {
    const set = new Set<string>();
    for (const r of rows) {
      const c = r.Category.trim();
      if (c) {set.add(c);}
    }
    return [...set].sort();
  });

  const filteredRows = createMemo(() => {
    const all = rows;
    if (all.length === 0) {return [] as AlarmRow[];}

    const filter = categoryFilter();
    const term = searchTerm().trim().toLowerCase();
    const brush = getBoundingMsFromSelectedRange(selectedRange());

    return all.filter((r) => {
      const category = r.Category.toLowerCase();
      if (filter === FILTER_ALARMS_ONLY) {
        if (category === 'info') {return false;}
      } else if (filter !== FILTER_ALL && category !== filter.toLowerCase()) {
        return false;
      }

      if (term) {
        const iso = new Date(r.ts * 1000).toISOString().toLowerCase();
        const hay = `${r.Category} ${r.Source} ${r.Description} ${iso}`.toLowerCase();
        if (!hay.includes(term)) {return false;}
      }

      if (brush) {
        const ms = r.ts * 1000;
        if (ms < brush.startMs || ms > brush.endMs) {return false;}
      }
      return true;
    });
  });

  // Reset to the first page whenever the filtered set changes shape.
  createEffect(() => {
    void filteredRows().length;
    void searchTerm();
    void categoryFilter();
    setPageIndex(0);
  });

  const pageCount = createMemo(() => Math.max(1, Math.ceil(filteredRows().length / PAGE_SIZE)));
  const pageRows = createMemo(() => {
    const p = Math.min(pageIndex(), pageCount() - 1);
    return filteredRows().slice(p * PAGE_SIZE, p * PAGE_SIZE + PAGE_SIZE);
  });

  /* ---- map markers ------------------------------------------------------------------------ */

  /**
   * Markers mirror the table, then are clipped to the playback time window so the map only shows
   * alarms from the span it is currently displaying — an alarm outside the window is hidden rather
   * than drawn on a stretch of track that is not there.
   *
   * Read reactively (not inside the rAF) so the markers follow the playhead as it moves.
   * `shouldApplyPlaybackTimeWindow()` already returns false while a brush or cut is active, and the
   * brush is applied in `filteredRows()`, so the two narrowings can never double-apply. A window of
   * 0 ("full") also disables it.
   *
   * This clips the MAP only — the table keeps showing the whole filtered set. Alinghi coupled the
   * two, so ticking a map option silently changed which rows the table listed.
   */
  createEffect(() => {
    const on = showOnMap();
    const subset = filteredRows();
    const applyWindow = shouldApplyPlaybackTimeWindow();
    const windowMinutes = timeWindow();
    const referenceTime = getDisplayWindowReferenceTime() ?? selectedTime();
    const gen = fetchGeneration;

    // One frame behind the table so the rows paint first.
    requestAnimationFrame(() => {
      if (gen !== fetchGeneration) {return;}
      if (!on) {
        lastMarkerSignature = '';
        setAlarmsExploreMapMarkers([]);
        return;
      }

      const bounds = applyWindow
        ? getPlaybackTimeWindowBoundsMs({ windowMinutes, referenceTime })
        : null;
      const visible = bounds
        ? subset.filter((r) => {
            const ms = r.ts * 1000;
            return ms >= bounds.startMs && ms <= bounds.endMs;
          })
        : subset;

      // While playing, the reference time above changes every animation frame, so this effect runs
      // 5-10 times a second. Publishing an identical array each time would make the map layer redraw
      // that often for nothing - which is what made the markers flicker. Only publish on a real
      // change; the ids are content-derived, so the signature is stable across redraws.
      const signature = `${visible.length}|${visible.length ? `${visible[0].ts}|${visible[visible.length - 1].ts}` : ''}`;
      if (signature === lastMarkerSignature) {return;}
      lastMarkerSignature = signature;

      setAlarmsExploreMapMarkers(
        buildAlarmExploreMapMarkers(
          visible.map((r) => ({
            ts: r.ts,
            source: r.Source,
            category: r.Category,
            description: r.Description,
          }))
        )
      );
    });
  });

  /* ---- interaction ------------------------------------------------------------------------ */

  /**
   * Jump the playhead to an alarm.
   *
   * Live and historical need different machinery. On a historical page taking time control and
   * writing `selectedTime` is enough. On a LIVE page it did nothing at all, twice over: the live map
   * holds time control as 'playback' for the whole session and 'alarms' had no listed priority (so
   * it resolved to 0 and lost), and even once granted, `setSelectedTime` refuses non-'playback'
   * writes while the clock is running. `seekLive` handles both, and also drops `liveFollow` so the
   * live tick's auto-resume does not undo the jump a couple of seconds later.
   */
  const seekTo = (date: Date) => {
    const ms = date.getTime();
    if (!Number.isFinite(ms)) {return;}

    if (liveMode()) {
      seekLive(ms, 'alarms');
      return;
    }

    if (requestTimeControl('alarms')) {
      setIsManualTimeChange(true);
      setSelectedTime(date, 'alarms');
      setTimeout(() => releaseTimeControl('alarms'), 100);
    }
  };

  const onDatetimeClick = (row: AlarmRow, e: MouseEvent) => {
    const date = new Date(row.ts * 1000);
    if (Number.isNaN(date.getTime())) {return;}
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const focusMs = date.getTime();
      batch(() => {
        setSelectedRange([
          {
            start_time: new Date(focusMs - CTRL_SEEK_HALF_SPAN_MS).toISOString(),
            end_time: new Date(focusMs + CTRL_SEEK_HALF_SPAN_MS).toISOString(),
          },
        ]);
        setHasSelection(true);
      });
      setTriggerSelection(true);
      // After the brush lands, so the playhead moves into an already-applied selection.
      queueMicrotask(() => seekTo(date));
      return;
    }
    seekTo(date);
  };

  /* ---- pager reveal ------------------------------------------------------------------------ */

  const recomputeAtBottom = () => {
    const el = tableScrollEl;
    if (!el) {return;}
    const noOverflow = el.scrollHeight <= el.clientHeight + TABLE_BOTTOM_SLACK_PX;
    setTableAtBottom(noOverflow || el.scrollTop + el.clientHeight >= el.scrollHeight - TABLE_BOTTOM_SLACK_PX);
  };

  createEffect(() => {
    void pageRows();
    void filteredRows().length;
    const el = tableScrollEl;
    if (!el) {
      setTableAtBottom(false);
      return;
    }
    const onScroll = () => recomputeAtBottom();
    el.addEventListener('scroll', onScroll, { passive: true });
    queueMicrotask(recomputeAtBottom);
    onCleanup(() => el.removeEventListener('scroll', onScroll));
  });

  /* ---- render ------------------------------------------------------------------------------ */

  const categoryPillClass = (category: string) => {
    const c = resolveAlarmCategory(category);
    return `alarm-category-pill alarm-category-pill--${c}`;
  };

  const rangeLabel = () => {
    const total = filteredRows().length;
    if (total === 0) {return '0';}
    const start = pageIndex() * PAGE_SIZE + 1;
    const end = Math.min(total, start + PAGE_SIZE - 1);
    return `${start}–${end} of ${total}`;
  };

  return (
    <div class={`alarms-explore-page${isFleet() ? '' : ' alarms-explore-page--no-source'}`}>
      <div class="alarms-explore-page__toolbar">
        <div class="alarms-explore-page__search">
          <FiSearch class="alarms-explore-page__search-icon" size={14} />
          <input
            type="search"
            value={searchTerm()}
            onInput={(e) => setSearchTerm(e.currentTarget.value)}
            placeholder="Search category, description, time…"
            aria-label="Search alarms"
          />
        </div>

        <div class="alarms-explore-page__severity-and-map">
          <select
            value={categoryFilter()}
            onChange={(e) => setCategoryFilter(e.currentTarget.value)}
            aria-label="Filter alarms by category"
          >
            <option value={FILTER_ALARMS_ONLY}>Warnings &amp; Penalties</option>
            <option value={FILTER_ALL}>All categories</option>
            <For each={uniqueCategories()}>{(c) => <option value={c}>{c}</option>}</For>
          </select>

          <label class="alarms-explore-page__map-markers-toggle">
            <input
              type="checkbox"
              checked={showOnMap()}
              onChange={(e) => setShowOnMap(e.currentTarget.checked)}
              aria-label="Draw alarms on the map"
            />
            <span>Show on map</span>
          </label>
        </div>

        <div class="alarms-explore-page__record-count">
          {loading() ? '—' : `${filteredRows().length} record(s)`}
        </div>
      </div>

      <Show when={loading()}>
        <div class="alarms-explore-page__loading">Loading alarms…</div>
      </Show>

      <Show when={!loading() && loadError()}>
        <div class="alarms-explore-page__error" role="alert">{loadError()}</div>
      </Show>

      <Show when={!loading() && !loadError() && rows.length === 0}>
        <div class="alarms-explore-page__empty">No alarms recorded.</div>
      </Show>

      <Show when={!loading() && !loadError() && rows.length > 0 && filteredRows().length === 0}>
        <div class="alarms-explore-page__empty">No rows match your search or category filter.</div>
      </Show>

      <Show when={!loading() && !loadError() && filteredRows().length > 0}>
        <div class="alarms-explore-page__main">
          <div class="alarms-explore-page__table-wrapper" ref={(el) => (tableScrollEl = el)}>
            <table class="alarms-explore-page__table" aria-label="Alarms">
              <thead>
                <tr>
                  <th>Datetime</th>
                  <th>Category</th>
                  <Show when={isFleet()}><th>Source</th></Show>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <For each={pageRows()}>
                  {(row) => (
                    <tr class="alarm-row">
                      <td
                        class="alarm-row__datetime"
                        role="button"
                        tabIndex={0}
                        title={`Jump to ${new Date(row.ts * 1000).toISOString()}`}
                        onClick={(e) => onDatetimeClick(row, e)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {onDatetimeClick(row, e as unknown as MouseEvent);}
                        }}
                      >
                        {formatDateTime(new Date(row.ts * 1000).toISOString())}
                      </td>
                      <td class="alarm-row__category">
                        <Show
                          when={row.Category}
                          fallback={<span class="alarm-category-pill alarm-category-pill--muted">—</span>}
                        >
                          <span class={categoryPillClass(row.Category)}>{row.Category}</span>
                        </Show>
                      </td>
                      <Show when={isFleet()}>
                        <td class="alarm-row__source">{row.Source || '—'}</td>
                      </Show>
                      <td class="alarm-row__description">{row.Description || '—'}</td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>

          <Show when={tableAtBottom() && pageCount() > 1}>
            <div class="alarms-explore-page__pager" role="navigation" aria-label="Alarm table pages">
              <div class="alarms-explore-page__pager-row">
                <span class="alarms-explore-page__pager-label">{rangeLabel()}</span>
                <div class="alarms-explore-page__pager-actions">
                  <button
                    class="btn btn-secondary"
                    disabled={pageIndex() === 0}
                    onClick={() => setPageIndex(0)}
                  >First</button>
                  <button
                    class="btn btn-secondary"
                    disabled={pageIndex() === 0}
                    onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                  >Previous</button>
                  <button
                    class="btn btn-secondary"
                    disabled={pageIndex() >= pageCount() - 1}
                    onClick={() => setPageIndex((p) => Math.min(pageCount() - 1, p + 1))}
                  >Next</button>
                  <button
                    class="btn btn-secondary"
                    disabled={pageIndex() >= pageCount() - 1}
                    onClick={() => setPageIndex(pageCount() - 1)}
                  >Last</button>
                </div>
              </div>
            </div>
          </Show>
        </div>
      </Show>
    </div>
  );
}
